import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  ShoppingCart,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  User,
  Building,
  FileText,
  Pencil,
  Send,
  Truck,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { FileUpload } from '@/components/ui/file-upload';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { usePermission } from '@/components/guards/permission-guard';

const statusConfig: Record<string, { color: string; icon: any }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  PENDING_APPROVAL: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  APPROVED: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  PARTIALLY_RECEIVED: { color: 'bg-purple-100 text-purple-700', icon: Package },
  FULLY_RECEIVED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { color: 'bg-red-100 text-red-700', icon: XCircle },
};

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canEdit = usePermission('PURCHASE_ORDERS', 'EDIT');

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<{ fileUrl: string; fileKey: string } | null>(null);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const {
    data: order,
    isLoading,
    refetch,
  } = trpc.tradingPurchaseOrder.byId.useQuery(id!, { enabled: !!id });

  const utils = trpc.useUtils();

  const submitMutation = trpc.tradingPurchaseOrder.submit.useMutation({
    onSuccess: () => {
      utils.tradingPurchaseOrder.byId.invalidate(id);
      utils.tradingPurchaseOrder.list.invalidate();
      showSuccessToast(t('purchasing.purchaseOrders.submitted'));
      setInvoiceNumber('');
      setInvoiceFile(null);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const approveMutation = trpc.tradingPurchaseOrder.approve.useMutation({
    onSuccess: () => {
      utils.tradingPurchaseOrder.byId.invalidate(id);
      utils.tradingPurchaseOrder.list.invalidate();
      showSuccessToast(t('purchasing.purchaseOrders.approvedSuccess'));
      setApproveConfirmOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const cancelMutation = trpc.tradingPurchaseOrder.cancel.useMutation({
    onSuccess: () => {
      utils.tradingPurchaseOrder.byId.invalidate(id);
      utils.tradingPurchaseOrder.list.invalidate();
      showSuccessToast(t('purchasing.purchaseOrders.cancelledSuccess'));
      setCancelConfirmOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('purchasing.purchaseOrders.draft');
      case 'PENDING_APPROVAL':
        return t('purchasing.purchaseOrders.pendingApproval');
      case 'APPROVED':
        return t('purchasing.purchaseOrders.approved');
      case 'PARTIALLY_RECEIVED':
        return t('purchasing.purchaseOrders.partiallyReceived');
      case 'FULLY_RECEIVED':
        return t('purchasing.purchaseOrders.fullyReceived');
      case 'CANCELLED':
        return t('purchasing.purchaseOrders.cancelled');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleSubmit = () => {
    if (!id || !invoiceNumber.trim()) return;
    submitMutation.mutate({
      id,
      invoiceNumber: invoiceNumber.trim(),
      fileUrl: invoiceFile?.fileUrl,
      fileKey: invoiceFile?.fileKey,
    });
  };

  const handleApprove = () => {
    if (!id) return;
    approveMutation.mutate(id);
  };

  const handleCancel = () => {
    if (!id) return;
    cancelMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">{tCommon('notFound')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('purchasing.purchaseOrders.notFoundDesc')}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/trading/purchasing')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('purchasing.purchaseOrders.backToList')}
        </Button>
      </div>
    );
  }

  const config = statusConfig[order.status] || statusConfig.PENDING_APPROVAL;
  const StatusIcon = config?.icon ?? Clock;

  const existingInvoice = order.supplierInvoices?.[0] || null;
  const canSubmit = order.status === 'PENDING_APPROVAL' && order.items.length > 0;
  const canApprove = order.status === 'PENDING_APPROVAL';
  const canReceiveGoods =
    order.status === 'APPROVED' || order.status === 'PARTIALLY_RECEIVED';
  const canCancelOrder =
    (order.status === 'PENDING_APPROVAL' ||
      order.status === 'APPROVED') &&
    order.receipts.length === 0;
  // Editing stays available through partial receipt (adding lines, raising
  // quantities is legitimate); the server rejects changes to received lines
  // and to the currency. Fully received / cancelled orders are frozen.
  const canEditOrder =
    order.status !== 'FULLY_RECEIVED' && order.status !== 'CANCELLED';

  const totalItems = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const totalReceived = order.items.reduce((sum, item) => sum + item.receivedQty, 0);
  const totalPending = order.items.reduce((sum, item) => sum + item.pendingQty, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/trading/purchasing')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
              >
                <StatusIcon className="h-3 w-3" />
                {getStatusLabel(order.status)}
              </span>
            </div>
            <p className="text-muted-foreground">
              {order.supplier.companyName} - {order.warehouse.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && canEditOrder && (
            <Button variant="outline" onClick={() => navigate(`/trading/purchasing/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              {tCommon('edit')}
            </Button>
          )}
          {canEdit && canSubmit && invoiceNumber.trim() && (
            <Button
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              {t('purchasing.purchaseOrders.submit')}
            </Button>
          )}
          {canEdit && canApprove && (
            <Button onClick={() => setApproveConfirmOpen(true)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              {t('purchasing.purchaseOrders.approve')}
            </Button>
          )}
          {canEdit && canReceiveGoods && (
            <Button onClick={() => navigate(`/trading/purchasing/receipts/new?poId=${id}`)}>
              <Package className="mr-2 h-4 w-4" />
              {t('purchasing.purchaseOrders.receive')}
            </Button>
          )}
          {canEdit && canCancelOrder && (
            <Button variant="destructive" onClick={() => setCancelConfirmOpen(true)}>
              <Ban className="mr-2 h-4 w-4" />
              {tCommon('cancel')}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={approveConfirmOpen}
        onOpenChange={setApproveConfirmOpen}
        title={t('purchasing.purchaseOrders.approveTitle')}
        description={t('purchasing.purchaseOrders.approveDescription')}
        confirmLabel={t('purchasing.purchaseOrders.approve')}
        onConfirm={handleApprove}
        isLoading={approveMutation.isPending}
      />

      <ConfirmationDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title={t('purchasing.purchaseOrders.cancelTitle')}
        description={t('purchasing.purchaseOrders.cancelDescription')}
        confirmLabel={tCommon('cancel')}
        variant="destructive"
        onConfirm={handleCancel}
        isLoading={cancelMutation.isPending}
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('purchasing.purchaseOrders.subtotal')}</span>
          </div>
          <div className="text-2xl font-bold">{formatCurrency(order.subtotal, order.currency)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('purchasing.purchaseOrders.tax')}</span>
          </div>
          <div className="text-2xl font-bold">{formatCurrency(order.taxAmount, order.currency)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('purchasing.purchaseOrders.total')}</span>
          </div>
          <div className="text-2xl font-bold text-primary">
            {formatCurrency(order.total, order.currency)}
          </div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="pb-2">
            <span className="text-sm font-medium">{t('purchasing.purchaseOrders.progress')}</span>
          </div>
          <div className="text-2xl font-bold">
            {totalReceived}/{totalItems}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({totalPending} {t('purchasing.purchaseOrders.pending')})
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Order Details & Items */}
        <div className="space-y-6 lg:col-span-2">
          {/* Order Details Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {t('purchasing.purchaseOrders.orderDetails')}
              </h2>
            </div>
            <div className="p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.orderNumber')}
                  </Label>
                  <p className="font-mono font-medium">{order.orderNumber}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.status')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.orderDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p>{formatDate(order.orderDate)}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.expectedDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p>{formatDate(order.expectedDate)}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.paymentTerms')}
                  </Label>
                  <p>{order.paymentTerms || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.deliveryTerms')}
                  </Label>
                  <p>{order.deliveryTerms || '-'}</p>
                </div>
              </div>

              {order.notes && (
                <div className="mt-4 pt-4 border-t border-border">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.notes')}
                  </Label>
                  <p className="mt-1 text-sm">{order.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Items Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Package className="h-5 w-5" />
                  {t('purchasing.purchaseOrders.orderItems')}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {order.items.length} {t('purchasing.purchaseOrders.items')}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('purchasing.purchaseOrders.product')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.quantity')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.received')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.pending')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.unitPrice')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.taxRate')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('purchasing.purchaseOrders.lineTotal')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {order.items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{item.productName}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {item.productCode}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-green-600">{item.receivedQty}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{item.pendingQty}</td>
                      <td className="px-4 py-3 text-right">
                        {formatCurrency(item.unitPrice, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-right">{item.taxRate}%</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(item.lineTotal, order.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30">
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-right font-medium">
                      {t('purchasing.purchaseOrders.subtotal')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(order.subtotal, order.currency)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-right font-medium">
                      {t('purchasing.purchaseOrders.tax')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(order.taxAmount, order.currency)}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td colSpan={6} className="px-4 py-3 text-right font-bold">
                      {t('purchasing.purchaseOrders.total')}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-primary">
                      {formatCurrency(order.total, order.currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Goods Receipts Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Truck className="h-5 w-5" />
                  {t('purchasing.purchaseOrders.goodsReceipts')}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {order.receipts.length} {t('purchasing.purchaseOrders.receipts')}
                </span>
              </div>
            </div>
            <div className="p-4">
              {order.receipts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="mx-auto h-8 w-8 text-muted-foreground/50" />
                  <p className="mt-2">{t('purchasing.purchaseOrders.noReceipts')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {order.receipts.map((receipt) => (
                    <div
                      key={receipt.id}
                      className="flex items-center justify-between border border-border p-3 hover:bg-muted/30"
                    >
                      <div>
                        <Link
                          to={`/trading/purchasing/receipts/${receipt.id}`}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {receipt.receiptNumber}
                        </Link>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(receipt.receiptDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {receipt._count.items} {t('purchasing.purchaseOrders.items')}
                          </span>
                          {receipt.createdBy && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {receipt.createdBy.firstName} {receipt.createdBy.lastName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Supplier & Warehouse Info */}
        <div className="space-y-6">
          {/* Invoice Info Card */}
          <div className="border border-border bg-card rounded-md">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {t('purchasing.purchaseOrders.invoiceInfo')}
              </h2>
            </div>
            <div className="p-4 space-y-4">
              {existingInvoice ? (
                <>
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs">{t('purchasing.purchaseOrders.invoiceNumber')}</Label>
                    <p className="font-mono font-medium">{existingInvoice.invoiceNumber}</p>
                  </div>
                  {existingInvoice.fileUrl && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs">{t('purchasing.purchaseOrders.invoiceFile')}</Label>
                      <a
                        href={existingInvoice.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {t('purchasing.purchaseOrders.fileUploaded')}
                      </a>
                    </div>
                  )}
                </>
              ) : canSubmit ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="invoiceNumber">{t('purchasing.purchaseOrders.invoiceNumber')} *</Label>
                    <Input
                      id="invoiceNumber"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder={t('purchasing.purchaseOrders.invoiceNumberPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('purchasing.purchaseOrders.invoiceFile')}</Label>
                    <FileUpload
                      folder="supplier-invoices"
                      accept=".pdf,.jpg,.jpeg,.png"
                      maxSizeMB={10}
                      onUploaded={(result) => setInvoiceFile({ fileUrl: result.fileUrl, fileKey: result.fileKey })}
                      onRemoved={() => setInvoiceFile(null)}
                    />
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleSubmit}
                    disabled={!invoiceNumber.trim() || submitMutation.isPending}
                  >
                    {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Send className="mr-2 h-4 w-4" />
                    {t('purchasing.purchaseOrders.submit')}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('purchasing.purchaseOrders.noInvoice')}
                </p>
              )}
            </div>
          </div>

          {/* Supplier Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <User className="h-5 w-5" />
                {t('purchasing.purchaseOrders.supplier')}
              </h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t('purchasing.purchaseOrders.companyName')}
                </Label>
                <Link
                  to={`/trading/suppliers/${order.supplier.id}`}
                  className="block font-medium text-primary hover:underline"
                >
                  {order.supplier.companyName}
                </Link>
              </div>
              {order.supplier.contactName && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.contact')}
                  </Label>
                  <p>{order.supplier.contactName}</p>
                </div>
              )}
              {(order.supplier as any).email && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.email')}
                  </Label>
                  <p className="text-sm">{(order.supplier as any).email}</p>
                </div>
              )}
              {(order.supplier as any).phone && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.phone')}
                  </Label>
                  <p className="text-sm">{(order.supplier as any).phone}</p>
                </div>
              )}
            </div>
          </div>

          {/* Warehouse Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Building className="h-5 w-5" />
                {t('purchasing.purchaseOrders.warehouse')}
              </h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t('purchasing.purchaseOrders.warehouseName')}
                </Label>
                <p className="font-medium">{order.warehouse.name}</p>
              </div>
              {order.warehouse.code && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.warehouseCode')}
                  </Label>
                  <p className="font-mono text-sm">{order.warehouse.code}</p>
                </div>
              )}
              {order.warehouse.address && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('purchasing.purchaseOrders.address')}
                  </Label>
                  <p className="text-sm">{order.warehouse.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Created By Card */}
          {order.createdBy && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <User className="h-5 w-5" />
                  {t('purchasing.purchaseOrders.createdBy')}
                </h2>
              </div>
              <div className="p-4 space-y-2">
                <p className="font-medium">
                  {order.createdBy.firstName} {order.createdBy.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{order.createdBy.email}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(order.createdAt)}</p>
              </div>
            </div>
          )}

          {/* Approved By Card */}
          {order.approvedBy && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <CheckCircle className="h-5 w-5" />
                  {t('purchasing.purchaseOrders.approvedBy')}
                </h2>
              </div>
              <div className="p-4 space-y-2">
                <p className="font-medium">
                  {order.approvedBy.firstName} {order.approvedBy.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{order.approvedBy.email}</p>
                <p className="text-xs text-muted-foreground">{formatDateTime(order.approvedAt)}</p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">{t('purchasing.purchaseOrders.quickActions')}</h2>
            </div>
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/trading/suppliers/${order.supplier.id}`)}
              >
                <User className="mr-2 h-4 w-4" />
                {t('purchasing.purchaseOrders.viewSupplier')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/trading/purchasing/receipts')}
              >
                <Truck className="mr-2 h-4 w-4" />
                {t('purchasing.purchaseOrders.viewReceipts')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/trading/purchasing')}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                {t('purchasing.purchaseOrders.backToList')}
              </Button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default PurchaseOrderDetailPage;
